import type { CSSProperties } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Crosshair } from 'lucide-react';
import { GameVariant, VARIANT_SPECS } from '@draughts/shared';
import PuzzlePreviewBoard from '../shared/PuzzlePreviewBoard';
import CorniceDamiera from '../shared/CorniceDamiera';
import { deriveCaptureSections, type CaptureSection } from '../../rules/deriveCaptureRules';
import { captureExample, type CaptureExample, type CaptureExampleId } from '../../rules/captureExamples';
import { buildRulesPath, isRulesLang, type RulesLang } from '../../seo/routes';
import './Rules.css';

interface RulesCapturePageProps {
  variant: GameVariant;
  /** Back to the variant's rules page. */
  onBack: () => void;
}

/** "22x13" for a capture, "22-19" for a quiet move — the usual draughts notation. */
function moveNotation(move: { from: number; to: number; captures: number[] }): string {
  return `${move.from}${move.captures.length ? 'x' : '-'}${move.to}`;
}

export default function RulesCapturePage({ variant, onBack }: RulesCapturePageProps) {
  const { t, i18n } = useTranslation();
  const lang: RulesLang = isRulesLang(i18n.language) ? i18n.language : 'en';

  const spec = VARIANT_SPECS[variant];
  const lower = variant.toLowerCase();
  const name = t(`game.variants.${lower}`);
  const sections = useMemo(() => deriveCaptureSections(spec), [spec]);

  // The four colours the two sibling rules pages set on every board wrapper, plus the
  // two `--color-square-*` the square labels read: `.board-coord` colours itself with
  // the OTHER square's colour, and those are different variables from `--rules-sq-*` —
  // without them every label would fall back to the generic wood palette.
  const boardVars = {
    '--rules-sq-dark': spec.ui.colors.darkSquare,
    '--rules-sq-light': spec.ui.colors.lightSquare,
    '--rules-piece-white': spec.ui.colors.whitePiece,
    '--rules-piece-black': spec.ui.colors.blackPiece,
    '--color-square-dark': spec.ui.colors.darkSquare,
    '--color-square-light': spec.ui.colors.lightSquare,
  } as CSSProperties;

  /** A diagram plus its caption and the move(s) the engine forces. */
  const diagram = (example: CaptureExample | undefined, captionKey: string | undefined) => {
    if (!example) return null;
    const forced = example.moves.length === 1;
    return (
      <figure className="rl-cap-fig">
        <div className="rl-board-wrap" style={boardVars}>
          <CorniceDamiera className="rl-cornice">
            <PuzzlePreviewBoard
              fen={example.fen}
              variant={lower}
              squareSize={34}
              className="rl-board-svg rl-cap-board"
              darkSquareClass="rules-sq-dark"
              lightSquareClass="rules-sq-light"
              markSquares={example.mark}
              // Forced, not the user's `boardNotation` preference: the caption below spells
              // the moves out as numbers ("23x14"), so an unnumbered — or algebraic — board
              // would contradict the very text it illustrates.
              notation="numeric"
              ariaLabel={`${t('rules.capture.diagramAlt')} — ${name}`}
            />
          </CorniceDamiera>
        </div>
        <figcaption className="rl-cap-cap">
          {captionKey && <span className="rl-cap-caption-txt">{t(captionKey)}</span>}
          <span className="rl-cap-moves">
            <span className="rl-cap-moves-lbl">
              {t(forced ? 'rules.capture.forced' : 'rules.capture.choices')}
            </span>
            {example.moves.map((m) => (
              <code key={moveNotation(m) + m.captures.join()} className="rl-cap-move">
                {moveNotation(m)}
              </code>
            ))}
          </span>
        </figcaption>
      </figure>
    );
  };

  const renderSection = (section: CaptureSection) => (
    <section key={section.id} className="rl-card rl-cap-sec">
      <h2 className="rl-cap-h">{t(section.titleKey)}</h2>

      {/* The lead sentences are the SAME strings the variant page shows on its
          "Captures" card — reused, never paraphrased, so the two never drift. */}
      {section.factKeys.length > 0 && (
        <ul className="rl-cap-facts">
          {section.factKeys.map((key) => (
            <li key={key}>{t(key)}</li>
          ))}
        </ul>
      )}

      {section.bodyKeys.map((key) => (
        <p key={key} className="rl-cap-p">
          {t(key)}
        </p>
      ))}

      {section.ladder && (
        <ol className="rl-cap-ladder">
          {section.ladder.map((step, i) => (
            <li key={step.key} className="rl-cap-step">
              <span className="rl-cap-step-n">{t('rules.capture.levelLabel', { n: i + 1 })}</span>
              <span className="rl-cap-step-rule">{t(step.key)}</span>
              <span className="rl-cap-step-why">{t(step.whyKey)}</span>
              {diagram(captureExample(variant, step.exampleId as CaptureExampleId), step.exampleCaptionKey)}
            </li>
          ))}
        </ol>
      )}

      {diagram(captureExample(variant, section.exampleId as CaptureExampleId), section.exampleCaptionKey)}
    </section>
  );

  return (
    <div className="rl-page">
      <div className="rl-inner">
        <div className="rl-crumb">
          <a
            href={buildRulesPath(lang, variant)}
            onClick={(e) => { e.preventDefault(); onBack(); }}
          >
            <span aria-hidden="true">←</span> {name}
          </a>
          <span className="rl-sep">/</span>
          <span className="rl-now">{t('rules.capture.title')}</span>
        </div>

        <div className="rl-card rl-hero rl-hero-3">
          <div className="rl-hero-deco">
            <div className="rl-hero-king" />
          </div>
          <div className="rl-hero-center">
            <span className="rl-eyebrow">{t('rules.capture.title')}</span>
            <h1 className="rl-hero-title">
              <span className="rl-flag" aria-hidden="true">
                {spec.ui.flagEmoji}
              </span>{' '}
              {name}
            </h1>
            <p className="rl-hero-sub">{t('rules.capture.heroSubtitle', { variant: name })}</p>
          </div>
          <div className="rl-hero-chip">
            <span className="rl-chip-ico">
              <Crosshair />
            </span>
            <span className="rl-chip-txt">
              <span className="rl-chip-v">{t('rules.theme.capture')}</span>
              <span className="rl-chip-l">{t('rules.eyebrow')}</span>
            </span>
          </div>
        </div>

        <div className="rl-card rl-cap-intro">
          <p className="rl-cap-p">{t('rules.capture.intro')}</p>
          <p className="rl-cap-src">{t('rules.capture.source')}</p>
        </div>

        {sections.map(renderSection)}
      </div>
    </div>
  );
}
